import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import TabNav from "components/TabNav";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useState } from "react";
import { updateLegislativeDrafting } from "services/legislativeService";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REGISTRAR_TABS, LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { formatDay } from "utils/helpers";
import LegislativeDraftDetail from "./fragments/leftPanel";
import LegislativeDraftRightPanel from "./fragments/rightPanel";
import { useTranslation } from "react-i18next";
import { appendOfficeStamp } from "services/stampService";

export default function RegistrarLegislativeDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("Research Finding");
    const [letterRefNum, setLetterRefNum] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [messenger_name, setMessengerName] = useState("");
    const [proposalFile, setProposalFile] = useState<File | null>(null);
    const [amendedDraftFile, setAmendedDraftFile] = useState<File | null>(null);
    const { showToast } = useToast();
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value)
    }
    
    const [formState, setFormState] = useState({ ...request });
    
    const handleFileChange = (file: File | null) => {
        setProposalFile(file);
    };
    
    const handleAmendedFileChange = (file: File | null) => {
        setAmendedDraftFile(file);
    };

    const handleSave = async () => {
        const data = new FormData();
        if (!formState.letter_reference_number) {
            // Submit letter reference number
            data.append(
                "current_status",
                LEGISLATIVE_DRAFTING_REQUEST_STATUS.cafffee_aproval.value
            );
            data.append(
                "forward_status",
                LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_registrar.value
            );
            data.append("letter_reference_number", letterRefNum);
        } else {
            // Submit files
            data.append(
                "current_status",
                LEGISLATIVE_DRAFTING_REQUEST_STATUS.cafffee_aproval.value
            );
            data.append(
                "forward_status",
                LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_director.value
            );
            if (proposalFile) {
                data.append("research_completion_letter_file", proposalFile);
                // Office stamp from HRMS: the backend stamps the PDF before storing it.
                await appendOfficeStamp(data);
            }
            if (amendedDraftFile) {
                // The backend stores the amended law as amended_law_file.
                data.append("amended_law_file", amendedDraftFile);
            }
        }

        try {
            const response = await updateLegislativeDrafting(formState.id, data);

            setFormState({ ...response });
            showToast(t("LegalResearchRespondedBackSuccess") || "Legal research responded back successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest") || "Failed to update legal research request.", "error");
        } finally {
            setSubmitting(false);
            setLetterRefNum("");
        }
    };

    return (
        <div className="flex-1">
            <ShowUI
                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value}
                currentStatus={formState.current_status}
                forwardStatus={formState.forward_status}
            >
                {activeTab === "Research Finding" &&
                    (
                            <div className="w-full">
                                {/* Registry Registration */}
                                <div className="flex flex-col w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
                                    <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                                    {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                                </p>
                                                <h3 className="text-white font-bold text-base">{t("RegistrarRegistration") || "Registry Registration"}</h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        {!formState.letter_reference_number ? (
                                            <>
                                                <label htmlFor="letter_reference_number" className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                                    {t("LetterReferenceNumber") || "Letter Reference Number"} <span className="text-[#9B0505]">*</span>
                                                </label>
                                                <p className="text-xs text-gray-500 mb-2">
                                                    {t("LetterReferenceNumberHint") || "The official registry reference number assigned to this request."}
                                                </p>
                                                <input
                                                    id="letter_reference_number"
                                                    type="text"
                                                    value={letterRefNum}
                                                    onChange={handleChange}
                                                    placeholder={t("LetterReferenceNumberPlaceholder") || "Eg. 12345"}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent text-sm"
                                                />
                                                <button onClick={handleSave} disabled={submitting} className="mt-4 w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                                                    {submitting ? (
                                                        <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                                    ) : (
                                                        <>
                                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            {t("SubmitReferenceNumber") || "Submit Reference Number"}
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        ) : formState.uploaded_files?.find((f: any) => f.document_type === "research_completion_letter") ? (
                                            <div className="flex items-center gap-2 text-green-700">
                                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm font-semibold">{t("RegistrationCompleted") || "Registration Completed"}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <label className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                                    {t("LegalDraftingCompletionLetter") || "Legal Drafting Completion Letter"}
                                                </label>
                                                <div className="relative mb-4">
                                                    <FileUpload onChange={handleFileChange} />
                                                </div>
                                                <label className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                                    {t("AmendedDraftFile") || "Amended Draft File"}
                                                </label>
                                                <div className="relative mb-4">
                                                    <FileUpload onChange={handleAmendedFileChange} />
                                                </div>
                                                <button onClick={handleSave} disabled={submitting} className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                                                    {submitting ? (
                                                        <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                                    ) : (
                                                        t("SubmitFiles") || "Submit Files"
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                }
            </ShowUI>
        </div>
    );
}